import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Scanner } from '@/components/Scanner';
import {
  ScanLine,
  Package,
  MapPin,
  ArrowRight,
  ArrowLeft,
  X,
  AlertTriangle,
  CheckCircle,
  Trash2,
  Clock
} from 'lucide-react';

interface ScanResult {
  type: 'unit' | 'bin';
  id: string;
  definition_id?: string;
  definition_name?: string;
  part_number?: string;
  manufacturer?: string;
  location_name?: string;
  location_id?: string;
  toolroom_name?: string;
  status?: string;
  quantity?: number;
  barcode?: string;
  label_code?: string;
}

interface Job {
  id: string;
  job_number: string;
  customer?: string;
  status: string;
}

interface Location {
  id: string;
  name: string;
  location_type: string;
  toolroom_name?: string;
}

export function Scan() {
  const queryClient = useQueryClient();
  const [scannedCode, setScannedCode] = useState<string | null>(null);
  const [showScanner, setShowScanner] = useState(true);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  // Look up scanned code
  const { data: scanResult, isLoading, error, refetch } = useQuery({
    queryKey: ['scan', scannedCode],
    queryFn: async () => {
      if (!scannedCode) return null;
      const res = await api.get<ScanResult>(`/tools/search/label/${encodeURIComponent(scannedCode)}`);
      if (res.success) return res.data;
      throw new Error(res.error || 'Not found');
    },
    enabled: !!scannedCode,
    retry: false,
  });

  const handleScan = (code: string) => {
    setScannedCode(code);
    setShowScanner(false);
  };

  const handleReset = () => {
    setScannedCode(null);
    setShowScanner(true);
  };

  const handleCheckoutSuccess = () => {
    setShowCheckoutModal(false);
    refetch();
    queryClient.invalidateQueries({ queryKey: ['my-checkouts'] });
  };

  const handleReturnSuccess = () => {
    setShowReturnModal(false);
    refetch();
    queryClient.invalidateQueries({ queryKey: ['my-checkouts'] });
  };

  const handleDiscardSuccess = () => {
    setShowDiscardModal(false);
    refetch();
    queryClient.invalidateQueries({ queryKey: ['my-checkouts'] });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Scan</h1>
          <p className="text-foreground-muted">Scan a tool or location barcode</p>
        </div>
        {scannedCode && (
          <Button variant="secondary" onClick={handleReset}>
            <ScanLine className="h-4 w-4" />
            Scan Another
          </Button>
        )}
      </div>

      {/* Scanner */}
      {showScanner && (
        <Card>
          <CardHeader>
            <h2 className="font-semibold">Camera Scanner</h2>
          </CardHeader>
          <CardBody>
            <Scanner onScan={handleScan} />
          </CardBody>
        </Card>
      )}

      {/* Manual Entry */}
      {showScanner && (
        <Card>
          <CardHeader>
            <h2 className="font-semibold">Manual Entry</h2>
          </CardHeader>
          <CardBody>
            <ManualEntry onSubmit={handleScan} />
          </CardBody>
        </Card>
      )}

      {/* Scan Result */}
      {scannedCode && (
        <Card>
          <CardHeader>
            <h2 className="font-semibold">Scan Result</h2>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <div className="text-center py-8">
                <div className="spinner h-8 w-8 mx-auto" />
                <p className="text-foreground-muted mt-2">Looking up code...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-warning" />
                <p className="text-lg font-medium mb-2">Not Found</p>
                <p className="text-foreground-muted mb-4">
                  No tool found for code: <code className="bg-surface-hover px-2 py-1 rounded">{scannedCode}</code>
                </p>
                <p className="text-sm text-foreground-muted mb-4">
                  This may be an old label or a typo. Print a new label from the Inventory page.
                </p>
                <Button onClick={handleReset}>Scan Again</Button>
              </div>
            ) : scanResult ? (
              <ScanResultDisplay
                result={scanResult}
                onCancel={handleReset}
                onCheckout={() => setShowCheckoutModal(true)}
                onReturn={() => setShowReturnModal(true)}
                onDiscard={() => setShowDiscardModal(true)}
              />
            ) : null}
          </CardBody>
        </Card>
      )}

      {/* Checkout Modal */}
      {scanResult && (
        <CheckoutModal
          isOpen={showCheckoutModal}
          onClose={() => setShowCheckoutModal(false)}
          onSuccess={handleCheckoutSuccess}
          toolUnitId={scanResult.type === 'unit' ? scanResult.id : undefined}
          definitionId={scanResult.definition_id}
          locationId={scanResult.location_id}
          toolName={scanResult.definition_name || 'Tool'}
        />
      )}

      {/* Return Modal */}
      {scanResult && (
        <ReturnModal
          isOpen={showReturnModal}
          onClose={() => setShowReturnModal(false)}
          onSuccess={handleReturnSuccess}
          toolUnitId={scanResult.type === 'unit' ? scanResult.id : undefined}
          toolName={scanResult.definition_name || 'Tool'}
        />
      )}

      {/* Discard Modal */}
      {scanResult && (
        <DiscardModal
          isOpen={showDiscardModal}
          onClose={() => setShowDiscardModal(false)}
          onSuccess={handleDiscardSuccess}
          toolUnitId={scanResult.type === 'unit' ? scanResult.id : undefined}
          toolName={scanResult.definition_name || 'Tool'}
        />
      )}

    </div>
  );
}

// Manual code entry component
function ManualEntry({ onSubmit }: { onSubmit: (code: string) => void }) {
  const [code, setCode] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.trim()) {
      onSubmit(code.trim());
      setCode('');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex gap-3">
      <input
        type="text"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder="Enter barcode or QR code..."
        className="flex-1 bg-surface border border-border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
      />
      <Button type="submit" disabled={!code.trim()}>
        Look Up
      </Button>
    </form>
  );
}

// Scan result display component
interface ScanResultDisplayProps {
  result: ScanResult;
  onCancel: () => void;
  onCheckout: () => void;
  onReturn: () => void;
  onDiscard: () => void;
}

function ScanResultDisplay({ result, onCancel, onCheckout, onReturn, onDiscard }: ScanResultDisplayProps) {
  if (result.type === 'unit') {
    return (
      <div className="space-y-6">
        {/* Tool Info */}
        <div className="flex items-start gap-4">
          <div className="p-3 bg-primary/10 rounded-lg">
            <Package className="h-8 w-8 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold">{result.definition_name}</h3>
            {result.manufacturer && (
              <p className="text-foreground-muted">{result.manufacturer}</p>
            )}
            {result.part_number && (
              <p className="text-sm text-foreground-muted font-mono">Part #: {result.part_number}</p>
            )}
            {result.label_code && (
              <p className="text-xs text-foreground-muted font-mono mt-1">Label: {result.label_code}</p>
            )}
          </div>
          <StatusBadge status={result.status} />
        </div>

        {/* Location */}
        {result.location_name && (
          <div className="flex items-center gap-2 text-foreground-muted">
            <MapPin className="h-4 w-4" />
            <span>{result.toolroom_name} &gt; {result.location_name}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
          {result.status === 'available' && (
            <Button className="flex-1" onClick={onCheckout}>
              <ArrowRight className="h-4 w-4" />
              Check Out
            </Button>
          )}
          {result.status === 'checked_out' && (
            <>
              <Button className="flex-1" onClick={onReturn}>
                <ArrowLeft className="h-4 w-4" />
                Return
              </Button>
              <Button variant="danger" onClick={onDiscard}>
                <Trash2 className="h-4 w-4" />
                Discard
              </Button>
            </>
          )}
          <Button variant="secondary" onClick={onCancel}>
            <X className="h-4 w-4" />
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  // Bin/Location result
  return (
    <div className="space-y-6">
      {/* Location Info */}
      <div className="flex items-start gap-4">
        <div className="p-3 bg-success/10 rounded-lg">
          <MapPin className="h-8 w-8 text-success" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold">{result.location_name}</h3>
          {result.toolroom_name && (
            <p className="text-foreground-muted">{result.toolroom_name}</p>
          )}
          {result.barcode && (
            <p className="text-sm text-foreground-muted font-mono">{result.barcode}</p>
          )}
        </div>
      </div>

      {/* Contents */}
      {result.definition_name && (
        <div className="bg-surface-hover rounded-lg p-4">
          <p className="text-sm text-foreground-muted uppercase tracking-wide mb-2">Contains</p>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{result.definition_name}</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold">{result.quantity || 0}</p>
              <p className="text-xs text-foreground-muted">in stock</p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-border">
        {result.definition_name && result.quantity && result.quantity > 0 && (
          <Button className="flex-1" onClick={onCheckout}>
            <ArrowRight className="h-4 w-4" />
            Take from Bin
          </Button>
        )}
        <Button variant="secondary" onClick={onCancel}>
          <X className="h-4 w-4" />
          Cancel
        </Button>
      </div>
    </div>
  );
}

// Checkout Modal
interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  toolUnitId?: string;
  definitionId?: string;
  locationId?: string;
  toolName: string;
}

function CheckoutModal({ isOpen, onClose, onSuccess, toolUnitId, definitionId, locationId, toolName }: CheckoutModalProps) {
  const [jobId, setJobId] = useState('');
  const [machine, setMachine] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [error, setError] = useState('');

  // Fetch active jobs
  const { data: jobs } = useQuery({
    queryKey: ['jobs', 'active'],
    queryFn: async () => {
      const res = await api.get<{ items: Job[] }>('/jobs?status=active&pageSize=100');
      if (res.success) return res.data?.items || [];
      return [];
    },
    enabled: isOpen,
  });

  const checkoutMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/checkouts', {
        toolUnitId,
        definitionId: !toolUnitId ? definitionId : undefined,
        locationId: !toolUnitId ? locationId : undefined,
        jobId: jobId || undefined,
        machine: machine || undefined,
        quantity: !toolUnitId ? parseInt(quantity) : 1,
      });
      if (!res.success) throw new Error(res.error || 'Checkout failed');
      return res.data;
    },
    onSuccess: () => {
      setError('');
      setJobId('');
      setMachine('');
      setQuantity('1');
      onSuccess();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Checkout failed');
    },
  });

  const jobOptions = [
    { value: '', label: 'No job (personal use)' },
    ...(jobs?.map(j => ({ value: j.id, label: `${j.job_number}${j.customer ? ` - ${j.customer}` : ''}` })) || []),
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Check Out: ${toolName}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => checkoutMutation.mutate()} loading={checkoutMutation.isPending}>
            <ArrowRight className="h-4 w-4" />
            Check Out
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-error bg-error/10 rounded-lg">{error}</div>
        )}

        <Select
          label="Assign to Job (optional)"
          options={jobOptions}
          value={jobId}
          onChange={(e) => setJobId(e.target.value)}
        />

        <Input
          label="Machine (optional)"
          value={machine}
          onChange={(e) => setMachine(e.target.value)}
          placeholder="e.g., VF-2, NLX-2500"
        />

        {!toolUnitId && (
          <Input
            label="Quantity"
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        )}
      </div>
    </Modal>
  );
}

// Return Modal
interface ReturnModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  toolUnitId?: string;
  toolName: string;
}

function ReturnModal({ isOpen, onClose, onSuccess, toolUnitId, toolName }: ReturnModalProps) {
  const [chipTimeHours, setChipTimeHours] = useState('');
  const [returnCondition, setReturnCondition] = useState('good');
  const [returnNotes, setReturnNotes] = useState('');
  const [returnLocationId, setReturnLocationId] = useState('');
  const [error, setError] = useState('');

  // Find the active checkout for this unit
  const { data: activeCheckout } = useQuery({
    queryKey: ['active-checkout', toolUnitId],
    queryFn: async () => {
      if (!toolUnitId) return null;
      const res = await api.get<{ items: { id: string }[] }>(`/checkouts?status=active&pageSize=1`);
      if (res.success && res.data?.items?.length) {
        return res.data.items[0];
      }
      return null;
    },
    enabled: isOpen && !!toolUnitId,
  });

  // Fetch locations for return
  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const res = await api.get<Location[]>('/locations');
      if (res.success) return res.data || [];
      return [];
    },
    enabled: isOpen,
  });

  const returnMutation = useMutation({
    mutationFn: async () => {
      if (!activeCheckout?.id) throw new Error('No active checkout found');
      if (!returnLocationId) throw new Error('Please select a return location');

      const res = await api.post(`/checkouts/${activeCheckout.id}/return`, {
        chipTimeHours: chipTimeHours ? parseFloat(chipTimeHours) : undefined,
        returnCondition,
        returnNotes: returnNotes || undefined,
        returnLocationId,
      });
      if (!res.success) throw new Error(res.error || 'Return failed');
      return res.data;
    },
    onSuccess: () => {
      setError('');
      setChipTimeHours('');
      setReturnCondition('good');
      setReturnNotes('');
      setReturnLocationId('');
      onSuccess();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Return failed');
    },
  });

  const locationOptions = [
    { value: '', label: 'Select location...' },
    ...(locations?.map(l => ({ value: l.id, label: `${l.toolroom_name ? l.toolroom_name + ' > ' : ''}${l.name}` })) || []),
  ];

  const conditionOptions = [
    { value: 'good', label: 'Good' },
    { value: 'worn', label: 'Worn' },
    { value: 'damaged', label: 'Damaged' },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Return: ${toolName}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => returnMutation.mutate()} loading={returnMutation.isPending}>
            <ArrowLeft className="h-4 w-4" />
            Return Tool
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-error bg-error/10 rounded-lg">{error}</div>
        )}

        <Select
          label="Return Location *"
          options={locationOptions}
          value={returnLocationId}
          onChange={(e) => setReturnLocationId(e.target.value)}
        />

        <Input
          label="Chip Time (hours)"
          type="number"
          step="0.1"
          min="0"
          value={chipTimeHours}
          onChange={(e) => setChipTimeHours(e.target.value)}
          placeholder="0.0"
        />

        <Select
          label="Condition"
          options={conditionOptions}
          value={returnCondition}
          onChange={(e) => setReturnCondition(e.target.value)}
        />

        <div>
          <label className="block text-sm font-medium mb-1">Notes</label>
          <textarea
            value={returnNotes}
            onChange={(e) => setReturnNotes(e.target.value)}
            placeholder="Any notes about the tool condition..."
            className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            rows={3}
          />
        </div>
      </div>
    </Modal>
  );
}

// Discard Modal
interface DiscardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  toolUnitId?: string;
  toolName: string;
}

function DiscardModal({ isOpen, onClose, onSuccess, toolUnitId, toolName }: DiscardModalProps) {
  const [discardReason, setDiscardReason] = useState('');
  const [chipTimeHours, setChipTimeHours] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  // Find the active checkout for this unit
  const { data: activeCheckout } = useQuery({
    queryKey: ['active-checkout-discard', toolUnitId],
    queryFn: async () => {
      if (!toolUnitId) return null;
      const res = await api.get<{ items: { id: string }[] }>(`/checkouts?status=active&pageSize=1`);
      if (res.success && res.data?.items?.length) {
        return res.data.items[0];
      }
      return null;
    },
    enabled: isOpen && !!toolUnitId,
  });

  const discardMutation = useMutation({
    mutationFn: async () => {
      if (!activeCheckout?.id) throw new Error('No active checkout found');

      const res = await api.post(`/checkouts/${activeCheckout.id}/discard`, {
        discardReason: discardReason || undefined,
        chipTimeHours: chipTimeHours ? parseFloat(chipTimeHours) : undefined,
        notes: notes || undefined,
      });
      if (!res.success) throw new Error(res.error || 'Discard failed');
      return res.data;
    },
    onSuccess: () => {
      setError('');
      setDiscardReason('');
      setChipTimeHours('');
      setNotes('');
      onSuccess();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Discard failed');
    },
  });

  const reasonOptions = [
    { value: '', label: 'Select reason...' },
    { value: 'worn', label: 'Normal wear' },
    { value: 'chipped', label: 'Chipped/broken' },
    { value: 'crashed', label: 'Machine crash' },
    { value: 'dulled', label: 'Dulled beyond regrind' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Discard: ${toolName}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="danger" onClick={() => discardMutation.mutate()} loading={discardMutation.isPending}>
            <Trash2 className="h-4 w-4" />
            Discard Tool
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-error bg-error/10 rounded-lg">{error}</div>
        )}

        <div className="p-3 bg-warning/10 border border-warning/30 rounded-lg flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-warning">This action cannot be undone</p>
            <p className="text-foreground-muted">The tool will be marked as discarded and removed from inventory.</p>
          </div>
        </div>

        <Select
          label="Discard Reason"
          options={reasonOptions}
          value={discardReason}
          onChange={(e) => setDiscardReason(e.target.value)}
        />

        <Input
          label="Chip Time (hours)"
          type="number"
          step="0.1"
          min="0"
          value={chipTimeHours}
          onChange={(e) => setChipTimeHours(e.target.value)}
          placeholder="0.0"
        />

        <div>
          <label className="block text-sm font-medium mb-1">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional notes about the discard..."
            className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            rows={3}
          />
        </div>
      </div>
    </Modal>
  );
}

function StatusBadge({ status }: { status?: string }) {
  switch (status) {
    case 'available':
      return (
        <Badge variant="success">
          <CheckCircle className="h-3 w-3 mr-1" />
          Available
        </Badge>
      );
    case 'checked_out':
      return (
        <Badge variant="warning">
          <Clock className="h-3 w-3 mr-1" />
          Checked Out
        </Badge>
      );
    case 'maintenance':
      return <Badge variant="info">Maintenance</Badge>;
    case 'discarded':
      return <Badge variant="error">Discarded</Badge>;
    default:
      return null;
  }
}

